<script setup lang="ts">
import { NavArrowLeft, EditPencil, Trash, OpenNewWindow, Plus } from "@iconoir/vue";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import LeadFormModal from "./LeadFormModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "LeadDetail" });

const { t, te } = useI18n();
const { d } = useFormat();
const route = useRoute();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

interface Lead {
  id: string;
  taxCode: string;
  companyName: string;
  contactName: string;
  legalRepresentative: string;
  phone: string;
  email: string;
  website: string;
  facebook: string;
  address: string;
  province: string;
  industry: string;
  industryCodes: string[];
  establishTime: number;
  hasBooking: boolean;
  note: string;
  status: string;
  score: number;
  tags: string[];
  source: string;
  sourceKey: string;
  sourceURL: string;
  assigned?: { id: string; fullName: string } | null;
  consentTime: number;
  createTime: number;
  updateTime: number;
}

const { formModel: lead, load, save, del, loadLoading, saveLoading, errorMsg } = useCrud<Lead>({
  endpoint: "/leads",
  defaultFormModel: () => ({}) as Lead,
});

const formRef = ref<InstanceType<typeof LeadFormModal> | null>(null);

const statusIntent: Record<string, string> = {
  new: "info",
  contacted: "neutral",
  replied: "warning",
  qualified: "warning",
  won: "positive",
  lost: "negative",
};

// Stored as 84xxxxxxxxx so dedupe and Zalo agree on one shape; nobody in
// Vietnam reads it that way.
const localPhone = computed(() => {
  const value = lead.phone || "";
  if (!value) return "";
  return value.startsWith("84") ? `0${value.slice(2)}` : value;
});

// tel: wants the international form even though the label shows the local one,
// so a tap from a phone dials correctly from anywhere.
const telHref = computed(() => (lead.phone ? `tel:+${lead.phone}` : ""));

const sourceLabel = computed(() => {
  if (lead.source === "crawler") return t("lead.sourceSystem");
  if (lead.source === "import") return t("lead.sourceImport");
  return t("lead.sourceManual");
});

// Notes are the one field a salesperson updates constantly — after a call, after
// a reply. Sending them through the full edit modal for one textarea is friction
// they would pay several times a day.
const noteEditing = ref(false);
const noteDraft = ref("");

function startNoteEdit() {
  noteDraft.value = lead.note || "";
  noteEditing.value = true;
}

async function saveNote() {
  const previous = lead.note;
  lead.note = noteDraft.value;
  try {
    await save();
    noteEditing.value = false;
    toast.success({ title: t("message.updateSuccess") });
  } catch {
    // Put the old text back so the card never shows a value that was not saved.
    lead.note = previous;
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

// zalo-* tags are outcomes rather than segments, so they read as status: sent
// is good, a dead number is a fact, a failed send is something to look at.
// Everything else is a segment and stays neutral.
const tagIntent: Record<string, string> = {
  "zalo-sent": "positive",
  "zalo-not-found": "negative",
  "zalo-failed": "warning",
};

// Known tags get a translated label; anything the team invented shows as typed.
function tagLabel(tag: string) {
  const key = `lead.tag_${tag.replace(/-/g, "_")}`;
  return te(key) ? t(key) : tag;
}

function externalHref(url: string) {
  if (!url) return "";
  return /^https?:\/\//.test(url) ? url : `https://${url}`;
}

async function confirmDelete() {
  const ok = await alert.negative({
    title: t("lead.deleteConfirmTitle"),
    description: t("lead.deleteConfirmBody", { name: lead.companyName }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(lead.id);
    toast.success({ title: t("message.deleteSuccess") });
    navigateTo("/leads");
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

onMounted(async () => {
  await load(route.params.id as string);
  setTitle(lead.companyName || t("lead.title"));
});
</script>

<template>
  <div class="flex flex-col gap-medium padding-bottom-medium md:padding-bottom-large">
    <div class="flex items-center gap-small">
      <Button variant="text-neutral" size="small" @click="navigateTo('/leads')">
        <NavArrowLeft class="icon" />
        {{ t("lead.backToList") }}
      </Button>
    </div>

    <div v-if="loadLoading" class="text-body-tertiary content-neutral-medium">{{ t("ui.loading") }}</div>

    <template v-else-if="lead.id">
      <Card>
        <div class="flex flex-col gap-medium md:flex-row md:items-start">
          <div class="flex flex-col flex-1 min-w-0 gap-xsmall">
            <div class="flex items-center gap-small flex-wrap">
              <h1 class="h3-primary">{{ lead.companyName }}</h1>
              <Badge :intent="statusIntent[lead.status] || 'neutral'" emphasis="medium" size="small">
                {{ t(`lead.status_${lead.status}`) }}
              </Badge>
              <Badge
                v-for="tag in lead.tags || []"
                :key="tag"
                :intent="tagIntent[tag] || 'neutral'"
                emphasis="medium"
                size="small"
              >
                {{ tagLabel(tag) }}
              </Badge>
            </div>
            <p class="text-body-tertiary content-neutral-medium">
              {{ sourceLabel }}<template v-if="lead.sourceKey"> · {{ lead.sourceKey }}</template>
            </p>
          </div>

          <div class="flex items-center gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.LeadsWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              @click="formRef?.open(lead.id)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>
            <ButtonIcon
              v-if="can(AdminPermission.LeadsDelete)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              @click="confirmDelete"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </div>
      </Card>

      <div class="lead-detail__grid">
        <SectionCard :title="t('lead.sectionContact')">

          <!-- Tappable: this table is worked from a phone as often as a desk,
               and a number you have to retype is a number that gets mistyped. -->
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("field.phone") }}</span>
            <a v-if="lead.phone" :href="telHref" class="lead-detail__value text-body-secondary lead-detail__link">{{ localPhone }}</a>
            <span v-else class="lead-detail__value text-body-secondary content-neutral-medium">—</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("field.email") }}</span>
            <a v-if="lead.email" :href="`mailto:${lead.email}`" class="lead-detail__value text-body-secondary lead-detail__link">
              {{ lead.email }}
            </a>
            <span v-else class="lead-detail__value text-body-secondary content-neutral-medium">—</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.contact") }}</span>
            <span class="lead-detail__value text-body-secondary">{{ lead.contactName || "—" }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.website") }}</span>
            <a
              v-if="lead.website"
              :href="externalHref(lead.website)"
              target="_blank"
              rel="noopener"
              class="lead-detail__value text-body-secondary lead-detail__link"
            >
              {{ lead.website }} <OpenNewWindow class="icon" />
            </a>
            <span v-else class="lead-detail__value text-body-secondary content-neutral-medium">—</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.facebook") }}</span>
            <a
              v-if="lead.facebook"
              :href="externalHref(lead.facebook)"
              target="_blank"
              rel="noopener"
              class="lead-detail__value text-body-secondary lead-detail__link"
            >
              {{ lead.facebook }} <OpenNewWindow class="icon" />
            </a>
            <span v-else class="lead-detail__value text-body-secondary content-neutral-medium">—</span>
          </div>
        </SectionCard>

        <SectionCard :title="t('lead.sectionBusiness')">

          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.taxCode") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">{{ lead.taxCode || "—" }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.legalRep") }}</span>
            <span class="lead-detail__value text-body-secondary">{{ lead.legalRepresentative || "—" }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.establishTime") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">
              {{ lead.establishTime ? d(lead.establishTime, "short") : "—" }}
            </span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.industry") }}</span>
            <span class="lead-detail__value text-body-secondary">{{ lead.industry || "—" }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.industryCodes") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">
              {{ (lead.industryCodes || []).join(", ") || "—" }}
            </span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.province") }}</span>
            <span class="lead-detail__value text-body-secondary">{{ lead.province || "—" }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.address") }}</span>
            <span class="lead-detail__value text-body-secondary">{{ lead.address || "—" }}</span>
          </div>
        </SectionCard>

        <SectionCard :title="t('lead.sectionPipeline')">

          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.score") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">{{ lead.score ?? 0 }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.owner") }}</span>
            <span class="lead-detail__value text-body-secondary">{{ lead.assigned?.fullName || t("lead.unassigned") }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.hasBooking") }}</span>
            <span class="lead-detail__value text-body-secondary">
              {{ lead.hasBooking ? t("lead.hasBookingYes") : t("lead.hasBookingNo") }}
            </span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.consentTime") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">
              {{ lead.consentTime ? d(lead.consentTime, "short") : "—" }}
            </span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("field.createTime") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">{{ d(lead.createTime, "datetime") }}</span>
          </div>
          <div class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.updateTime") }}</span>
            <span class="lead-detail__value text-body-secondary lead-detail__figure">
              {{ lead.updateTime ? d(lead.updateTime, "datetime") : "—" }}
            </span>
          </div>
          <div v-if="lead.sourceURL" class="lead-detail__row">
            <span class="lead-detail__label text-body-secondary content-neutral-medium">{{ t("lead.source") }}</span>
            <a
              :href="lead.sourceURL"
              target="_blank"
              rel="noopener"
              class="lead-detail__value text-body-secondary lead-detail__link"
            >
              {{ lead.sourceKey }} <OpenNewWindow class="icon" />
            </a>
          </div>
        </SectionCard>

        <SectionCard :title="t('lead.note')">
          <template #actions>
            <Button
              v-if="!noteEditing && can(AdminPermission.LeadsWrite)"
              variant="tertiary"
              size="small"
              @click="startNoteEdit"
            >
              <component :is="lead.note ? EditPencil : Plus" class="icon" />
              {{ t(lead.note ? "lead.editNote" : "lead.addNote") }}
            </Button>
          </template>

          <template v-if="noteEditing">
            <FormTextarea v-model="noteDraft" :rows="4" />
            <div class="flex items-center justify-end gap-xsmall">
              <Button variant="tertiary" size="small" @click="noteEditing = false">
                {{ t("action.cancel") }}
              </Button>
              <Button variant="primary" size="small" :disabled="saveLoading" @click="saveNote">
                {{ t("action.save") }}
              </Button>
            </div>
          </template>

          <p v-else class="lead-detail__note text-body-secondary" :class="{ 'content-neutral-medium': !lead.note }">
            {{ lead.note || "—" }}
          </p>
        </SectionCard>
      </div>
    </template>

    <LeadFormModal ref="formRef" @saved="load(route.params.id as string)" />
  </div>
</template>

<style scoped lang="scss">
.lead-detail__grid {
  display: grid;
  gap: token("gap-medium");
  grid-template-columns: 1fr;

  @include up("lg") {
    grid-template-columns: 1fr 1fr;
  }
}

.lead-detail__row {
  display: flex;
  flex-direction: column;
  gap: token("gap-3xsmall");

  @include up("md") {
    flex-direction: row;
    gap: token("gap-small");
  }
}

.lead-detail__label {
  flex: 0 0 40%;
}

.lead-detail__value {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.lead-detail__link {
  display: inline-flex;
  align-items: center;
  gap: token("gap-3xsmall");
  text-decoration: underline;
}

.lead-detail__note {
  white-space: pre-wrap;
}

.lead-detail__figure {
  font-variant-numeric: tabular-nums;
}


</style>
